import React, { useState } from "react";
import { filter } from "lodash";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import arrowFill from "@iconify/icons-eva/arrow-back-fill";

// material
import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Tooltip,
  IconButton,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import * as locales from "@material-ui/core/locale";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// components
import Page from "../components/Page";
import Label from "../components/Label";
import Scrollbar from "../components/Scrollbar";
import { TablaHead } from "../components/_dashboard/tablas";
import AgregarTarea from "../components/tipotareas/AgregarTarea";
import EditarTarea from "src/components/tipotareas/EditarTarea";
import { tiposApi } from "src/services/tiposApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { useHistorialVisita } from "src/hooks/useHistorialVisita";

// ==================== CONSTANTES ====================

const TABLE_HEAD = [
  { id: "nombre", label: "Nombre Tarea", alignRight: false },
  { id: "valor", label: "Valor / Moneda", alignRight: false },
  { id: "categoria", label: "Categoría", alignRight: false },
  { id: "activo", label: "Estado", alignRight: false },
  { id: "" },
];

const CATEGORIA_COLORS = {
  CONTABLE: { bg: "#e3f2fd", color: "#1565c0" },
  TRIBUTARIO: { bg: "#fce4ec", color: "#880e4f" },
  REMUNERACIONES: { bg: "#e8f5e9", color: "#2e7d32" },
  SOCIETARIO: { bg: "#fff3e0", color: "#e65100" },
  OTRO: { bg: "#f3e5f5", color: "#6a1b9a" },
};

const TIPO_TAREA_COLORS = {
  ESPECIAL: { bg: "#e8eaf6", color: "#283593" },
  NORMAL: { bg: "#e0f2f1", color: "#00695c" },
};

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #E0E0E0",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #F0F0F0",
    backgroundColor: "#FFFFFF",
  },
  statBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 18px",
    borderRadius: 8,
    backgroundColor: "#FAFAFA",
    border: "1px solid #F0F0F0",
    minWidth: 72,
  },
  statNumber: {
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: 1.2,
    color: "#212121",
  },
  statLabel: {
    fontSize: "0.72rem",
    color: "#9E9E9E",
    fontWeight: 500,
    marginTop: 2,
  },
  tableContainer: {
    "& .MuiTableCell-head": {
      backgroundColor: "#FAFAFA",
      fontWeight: 600,
      fontSize: "0.813rem",
      color: "#424242",
      borderBottom: "2px solid #E0E0E0",
      whiteSpace: "nowrap",
      padding: "14px 16px",
    },
  },
  tableRow: {
    height: 56,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#FAFAFA !important",
    },
  },
  tableCell: {
    fontSize: "0.875rem",
    color: "#212121",
    borderBottom: "1px solid #F5F5F5",
    padding: "12px 16px",
  },
  tableCellClickable: {
    cursor: "pointer",
  },
  nombreCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  tipoTareaBadge: {
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
  },
  categoriaBadge: {
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 10,
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  editButton: {
    color: "#BDBDBD",
    padding: 6,
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1976D2",
      backgroundColor: "#E3F2FD",
    },
  },
  deleteButton: {
    color: "#BDBDBD",
    padding: 6,
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#EF5350",
      backgroundColor: "#FFEBEE",
    },
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
}));

// ==================== HELPERS ====================

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (item) =>
        item.nombre.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        item.tipoTarea.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

// ==================== COMPONENTE PRINCIPAL ====================

export default function TipoTareas() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("orden");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [locale] = useState("esES");
  const { empleado: user } = useSelector((state) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();

  const { data: tipoTareas = [] } = tiposApi.useGetTipoTareasQuery();
  const { data: monedas = [] } = tiposApi.useGetTipoMonedaQuery();
  const [eliminarTipoTareaDB] = tiposApi.useDeleteTipoTareaMutation();

  useHistorialVisita(user);

  const statsEspecial = tipoTareas.filter(
    (t) => t.tipoTarea === "ESPECIAL",
  ).length;
  const statsNormal = tipoTareas.filter((t) => t.tipoTarea === "NORMAL").length;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(tipoTareas.map((n) => n.nombre));
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredTareas = applySortFilter(
    tipoTareas,
    getComparator(order, orderBy),
    filterName,
  );

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredTareas.length)
      : 0;

  const eliminarTarea = async (tipoTarea, nombre) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar esta tarea?",
      text: `Se eliminará "${nombre} (${tipoTarea})". Esta acción es irreversible.`,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#d32f2f",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarTipoTareaDB({ nombre, tipoTarea }).unwrap();
      enqueueSnackbar("Tarea eliminada correctamente", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al eliminar la tarea", { variant: "error" });
    }
  };

  return (
    <Page title="TAXTIC">
      <Container>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Button
            variant="contained"
            color="info"
            startIcon={<Icon icon={arrowFill} />}
            onClick={() => navigate("/dashboard/configuraciones")}
          >
            Volver
          </Button>
          <Typography variant="h4" gutterBottom>
            Tipo Tareas
          </Typography>
          <Button
            variant="contained"
            startIcon={<Icon icon={plusFill} />}
            onClick={() => setOpen(true)}
          >
            Agregar Tarea
          </Button>
        </Stack>

        <Card className={classes.card}>
          {/* Card Header: stats + buscador */}
          <Box className={classes.cardHeader}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              {/* Estadísticas */}
              <Stack direction="row" spacing={1.5}>
                <Box className={classes.statBox}>
                  <Typography className={classes.statNumber}>
                    {tipoTareas.length}
                  </Typography>
                  <Typography className={classes.statLabel}>Total</Typography>
                </Box>
                <Box
                  className={classes.statBox}
                  style={{ borderColor: "#C5CAE9" }}
                >
                  <Typography
                    className={classes.statNumber}
                    style={{ color: "#283593" }}
                  >
                    {statsEspecial}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    Especial
                  </Typography>
                </Box>
                <Box
                  className={classes.statBox}
                  style={{ borderColor: "#B2DFDB" }}
                >
                  <Typography
                    className={classes.statNumber}
                    style={{ color: "#00695c" }}
                  >
                    {statsNormal}
                  </Typography>
                  <Typography className={classes.statLabel}>Normal</Typography>
                </Box>
              </Stack>

              {/* Buscador */}
              <TextField
                size="small"
                placeholder="Buscar tarea..."
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  setPage(0);
                }}
                style={{ width: 260 }}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#9E9E9E", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  style: { borderRadius: 8, backgroundColor: "#FAFAFA" },
                }}
              />
            </Stack>
          </Box>

          {/* Tabla */}
          <Scrollbar>
            <TableContainer
              className={classes.tableContainer}
              sx={{ minWidth: 800 }}
            >
              <Table>
                <TablaHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tipoTareas.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredTareas
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => {
                      const {
                        nombre,
                        valor,
                        tipoMoneda,
                        tipoTarea,
                        categoria,
                        activo,
                      } = row;
                      const isItemSelected = selected.indexOf(nombre) !== -1;
                      const categoriaColor = CATEGORIA_COLORS[categoria] ?? {
                        bg: "#F5F5F5",
                        color: "#616161",
                      };
                      const tipoColor = TIPO_TAREA_COLORS[tipoTarea] ?? {
                        bg: "#F5F5F5",
                        color: "#616161",
                      };

                      const handleAbrir = () => {
                        setElementoSeleccionado(row);
                        setOpenEditar(true);
                      };

                      return (
                        <TableRow
                          key={nombre + "_" + idx}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          className={classes.tableRow}
                        >
                          {/* Nombre + badge tipoTarea */}
                          <TableCell className={classes.tableCell}>
                            <Box className={classes.nombreCell}>
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 600 }}
                              >
                                {nombre}
                              </Typography>
                              <span
                                className={classes.tipoTareaBadge}
                                style={{
                                  backgroundColor: tipoColor.bg,
                                  color: tipoColor.color,
                                }}
                              >
                                {tipoTarea}
                              </span>
                            </Box>
                          </TableCell>

                          {/* Valor + Moneda combinados */}
                          <TableCell className={classes.tableCell}>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 500 }}
                            >
                              {valor != null ? valor : "—"}
                            </Typography>
                            {tipoMoneda && (
                              <Typography
                                variant="caption"
                                style={{ color: "#9E9E9E" }}
                              >
                                {tipoMoneda}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Categoría */}
                          <TableCell className={classes.tableCell}>
                            {categoria ? (
                              <span
                                className={classes.categoriaBadge}
                                style={{
                                  backgroundColor: categoriaColor.bg,
                                  color: categoriaColor.color,
                                }}
                              >
                                {categoria}
                              </span>
                            ) : (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Estado */}
                          <TableCell className={classes.tableCell}>
                            <Label
                              color={activo ? "success" : "default"}
                              variant="ghost"
                            >
                              {activo ? "Activo" : "Inactivo"}
                            </Label>
                          </TableCell>

                          {/* Acciones */}
                          <TableCell
                            className={classes.tableCell}
                            align="right"
                          >
                            <Tooltip title="Editar">
                              <IconButton
                                className={classes.editButton}
                                onClick={handleAbrir}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                className={classes.deleteButton}
                                onClick={() => eliminarTarea(tipoTarea, nombre)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 56 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>

                {filteredTareas.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        className={classes.emptyState}
                      >
                        <Typography variant="body2" color="textSecondary">
                          {filterName
                            ? `Sin resultados para "${filterName}"`
                            : "No hay tareas registradas"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredTareas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>

      {open && <AgregarTarea open={open} setOpen={setOpen} monedas={monedas} />}
      {openEditar && elementoSeleccionado && (
        <EditarTarea
          tarea={elementoSeleccionado}
          openEditar={openEditar}
          setOpenEditar={setOpenEditar}
          monedas={monedas}
        />
      )}
    </Page>
  );
}
